import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { SmartLink } from "@/components/Header";
import { Band, Hero, In, JsonLd, List, P, Related, Steps, articleLd } from "@/components/seo/kit";

// /documentation: ClearMakes Inbox help. Rewritten in the new design on 2026-09-29, keeping the
// facts of the earlier page (which already matched today's app). SEO run: gtm/products/clearmakes-inbox/seo/.

const PATH = "/documentation";
const TITLE = "ClearMakes Inbox Documentation: Set-Up and Help";
const DESC = "ClearMakes Inbox help: sign in, connect Gmail, Outlook, Slack, Teams and more, describe a workflow, save Smart FAQ answers, and manage your plan.";

const SECTIONS = [
  ["#start", "Signing in"],
  ["#apps", "Connecting an app"],
  ["#workflow", "Building a workflow"],
  ["#runs", "Checking what ran"],
  ["#faq", "Smart FAQ"],
  ["#own-tools", "Your own tools"],
  ["#account", "Settings, plan and account"],
];

const Documentation = () => (
  <>
    <PageMeta
      title={TITLE}
      description={DESC}
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "ClearMakes Inbox", path: "/products/inbox" },
        { name: "Documentation", path: PATH },
      ]}
    />
    <JsonLd data={articleLd(TITLE, DESC, PATH)} />
    <main>
      <Hero
        eyebrow="ClearMakes Inbox · Documentation"
        title="ClearMakes Inbox documentation"
        lead="Short, plain guides to signing in, connecting apps, describing workflows, saving Smart FAQ answers and managing your plan."
        primary={{ label: "Open ClearMakes Inbox", href: "/login" }}
      >
        <nav aria-label="On this page" className="mt-8 flex flex-wrap gap-2">
          {SECTIONS.map(([href, label]) => (
            <a key={href} href={href} className="rounded-full border border-[hsl(0_0%_100%/0.25)] px-3.5 py-1.5 text-[14px] text-[hsl(0_0%_100%/0.85)] hover:bg-[hsl(0_0%_100%/0.1)]">
              {label}
            </a>
          ))}
        </nav>
      </Hero>

      <Band tone="white" title="What ClearMakes Inbox is" answer="ClearMakes Inbox connects the apps you use and runs workflows on new messages. It also answers everyday questions from answers you save, with Smart FAQ." narrow>
        <P>
          The app has five pages: <strong>Home</strong>, <strong>Workflows</strong>, <strong>Apps</strong>, <strong>Smart FAQ</strong> and <strong>Settings</strong>.
          The AI steps use Google Gemini. AI can make mistakes, so check what it writes, especially at first.
        </P>
      </Band>

      <Band tone="pale" id="start" title="Signing in" answer="Sign in at clearmakes.com/login, or create an account the first time. You land on Home, which shows how many workflows ran today and the most recent runs.">
        <Steps
          items={[
            { title: "Go to clearmakes.com/login", body: <SmartLink href="/login" className="underline underline-offset-4">Open the sign-in page</SmartLink> },
            { title: "Sign in, or create an account", body: "Signing in with Google or Microsoft shares only your email address and basic profile." },
            { title: "Start from Home", body: "It shows today's runs and the latest ones." },
          ]}
        />
      </Band>

      <Band tone="white" id="apps" title="Connecting an app" answer="Your workflows can only use apps you have connected. Open Apps, find the app, choose Connect, and sign in on that app's own page.">
        <Steps
          items={[
            { title: "Open the Apps page", body: "The most used apps are at the top; categories and a search cover thousands more." },
            { title: "Find the app and choose Connect", body: "Gmail, Outlook, Slack, Microsoft Teams, Notion, HubSpot, Google Sheets, Linear and more." },
            { title: "Sign in to that app", body: "The connection is made through Composio, which handles app connections for ClearMakes. The app shows its permissions before you agree." },
            { title: "Disconnect any time", body: "Connected apps are listed on the Apps page, each with a Disconnect button." },
          ]}
        />
        <P>
          Set-up guides for common pairs: <In href="/integrations/gmail-to-slack">Gmail to Slack</In>, <In href="/integrations/outlook-to-slack">Outlook to Slack</In>,{" "}
          <In href="/integrations/outlook-to-teams">Outlook to Teams</In>, <In href="/integrations/email-to-hubspot">email leads to HubSpot</In>.
        </P>
      </Band>

      <Band tone="pale" id="workflow" title="Building a workflow" answer="Describe what should happen in one sentence, and Inbox writes the steps. If it needs to know something, it asks one question, then you check the steps and switch the workflow on.">
        <Steps
          items={[
            { title: "Open Workflows and create a new one", body: "Under “What should happen?”, type a sentence, for example “Reply to anyone asking about pricing and label it Sales”." },
            { title: "Answer at most one question", body: "Only about behaviour, such as whether a reply should send by itself or wait for you." },
            { title: "Check the steps", body: "“Start it when” shows what starts it; each step below says what it does. You can change or remove any step, or add your own." },
            { title: "Name it, create it, and switch it on", body: "Switch any workflow on or off from the Workflows page." },
          ]}
        />
        <P>
          The full list of steps and 12 example sentences: <In href="/workflows">how workflows work</In>.
        </P>
      </Band>

      <Band tone="white" id="runs" title="Checking what ran" answer="Every run is listed with each step, whether it worked, and what went out. Use it to check a new workflow and to see where one stopped." narrow>
        <List
          items={[
            "Home shows today's runs; each workflow shows its recent runs.",
            "A run can go through, stop early (a check didn't match), or fail at a step, and it says which.",
            "Waiting steps (wait a while, wait for a reply) are saved, so they continue after updates.",
          ]}
        />
      </Band>

      <Band tone="pale" id="faq" title="Smart FAQ" answer="Smart FAQ holds the answers to the questions you get every day. Add a question and the answer you'd really send; when a new email asks it, Inbox replies with that answer." narrow>
        <Steps
          items={[
            { title: "Open the Smart FAQ page", body: "Starter ideas: prices, refunds, delivery time, booking a call." },
            { title: "Add a question and your answer", body: "Write the answer the way you would in a reply." },
            { title: "Check recent replies", body: "The page lists the questions it answered recently. Update an answer whenever something changes." },
          ]}
        />
        <P>
          More: <In href="/smart-faq">Smart FAQ</In> and the guide to <In href="/guides/auto-reply-to-common-customer-questions">auto-replying to common customer questions</In>.
        </P>
      </Band>

      <Band tone="white" id="own-tools" title="Your own tools" answer="Under Apps, “Something of your own” adds an MCP server or an HTTP endpoint, so a workflow step can call your own system." narrow>
        <List
          items={[
            "MCP server: Inbox finds its tools; new tools on your server appear in Inbox.",
            "HTTP endpoint: describe it once, with {{placeholders}} for the parts that change.",
            "Private network addresses are refused, and keys are stored encrypted and never shown back.",
          ]}
        />
      </Band>

      <Band tone="pale" id="account" title="Settings, plan and account" answer="Settings is where you change your name and theme, see and change your plan, manage payments, and delete your account." narrow>
        <List
          items={[
            "Account: change your name and the look of the app, or sign out. Your workflows keep running after you sign out.",
            <>Plan: see your plan and limits, switch plans, and open Payment and invoices. Payments are handled by Paddle. See <In href="/pricing">ClearMakes Inbox pricing</In>.</>,
            "Delete your account: in Settings, choose Delete my account and confirm. This deletes your ClearMakes account and everything in Inbox, and can't be undone. You can also email contact@clearmakes.com.",
            <>Your data: encrypted in transit (TLS) and at rest, and never used to train AI models. See <In href="/security-architecture">security</In> and the <In href="/guidelines">sub-processors</In>.</>,
          ]}
        />
      </Band>

      <Related
        links={[
          { href: "/guides", label: "Guides", desc: "Step-by-step guides for email and chat work." },
          { href: "/integrations", label: "Integrations", desc: "Every app your workflows can use." },
          { href: "/contact", label: "Contact", desc: "Can't find an answer? Write to us." },
        ]}
      />
    </main>
    <Footer />
  </>
);

export default Documentation;
